/**
 * Agenda e lembretes.
 *
 * ## Duas coisas, e a tela nunca as confunde
 *
 * **Mensagem** sai para o contato. **Lembrete** acende para a equipe e não sai para ninguém. A escolha é o
 * primeiro controle do formulário, e o resto do formulário muda com ela — misturar as duas numa palavra
 * só é o que faz alguém agendar um "lembrete" e o cliente dele receber uma mensagem.
 *
 * ## O aviso da janela aparece enquanto a pessoa escolhe o horário
 *
 * Mensagem agendada para depois de amanhã quase sempre cai fora da janela de 24 horas do WhatsApp, e aí
 * texto livre não sai — só template alcança. O aviso muda a cada mudança da data, **antes** de salvar.
 *
 * Quantas horas é a janela vem do servidor (é regra do canal, mora no catálogo). A frase é daqui, porque
 * frase é apresentação — e o servidor não decide microcópia.
 */


/*
 * A FORMA (spec 22, 31/10): família MESA DE TRABALHO, e não Consulta — a tela é o trabalho do dia (criar um
 * lembrete e marcar o feito), com o formulário e a lista lado a lado. `PaginaInteira` + `CabecalhoDaTela` +
 * `GradeDeCartoes` com dois cartões: o novo agendamento (o botão fica no rodapé, fora do que rola) e os agendados
 * (rolam por dentro). No celular, um cartão por vez.
 */

import { useId, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EstadoDaFalha, falhaDe } from "@hugi/ui";
import { CabecalhoDaTela, Carregando, Dica, GradeDeCartoes, Motivo, PaginaInteira, Selo, Vazio } from "@hugi/ui/kit";
import { CalendarClock } from "lucide-react";
import {
  cancelarAgendamento,
  concluirAgendamento,
  criarAgendamento,
  useAgenda,
  type ItemDaAgenda,
} from "./dados.js";
import "./hugi-agenda.css";

/** O valor que `<input type="datetime-local">` aceita, para "agora + minutos", no fuso do navegador. */
function paraCampoLocal(emMinutos: number): string {
  const d = new Date(Date.now() + emMinutos * 60_000);
  // `toISOString` daria UTC, e o campo é local — a diferença apareceria como três horas de erro em São
  // Paulo, no valor inicial do formulário.
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Pagina(props: { readonly children: ReactNode }) {
  return (
    <PaginaInteira rotulo="Agenda e lembretes" classe="hugi-agenda">
      <CabecalhoDaTela
        icone={<CalendarClock size={16} />}
        titulo="Agenda e lembretes"
        dica="Mensagem agendada sai para o contato no horário. Lembrete só acende aqui, para a equipe."
      />
      {props.children}
    </PaginaInteira>
  );
}

export function Agenda() {
  const cliente = useQueryClient();
  const q = useAgenda();
  const idDoMotivo = useId();

  const [tipo, setTipo] = useState<"mensagem" | "lembrete">("lembrete");
  const [quando, setQuando] = useState(() => paraCampoLocal(60));
  const [texto, setTexto] = useState("");
  const [atendimentoId, setAtendimentoId] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  if (q.isPending) return <Pagina><Carregando>Carregando a agenda</Carregando></Pagina>;
  if (q.isError) return <Pagina><EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} /></Pagina>;

  const { itens, canais, conversas } = q.data;

  /**
   * A menor janela entre os canais conectados.
   *
   * A menor, e não a do canal escolhido, porque nesta tela ainda não há canal escolhido: o agendamento
   * solto é por contato, e o canal é o da conversa dele. Avisar pelo caso mais restritivo é o que evita
   * "a tela não avisou e a mensagem não saiu".
   */
  const janelas = canais.map((c) => c.janelaHoras).filter((h): h is number => h !== null);
  const janela = janelas.length > 0 ? Math.min(...janelas) : null;

  const emMinutos = quando ? (new Date(quando).getTime() - Date.now()) / 60_000 : NaN;
  const passou = Number.isFinite(emMinutos) && emMinutos < 1;

  /**
   * A janela do canal DAQUELA conversa quando há uma escolhida; a menor entre os canais, quando não há.
   *
   * A menor no caso geral porque avisar pelo caso mais restritivo é o que evita "a tela não avisou e a
   * mensagem não saiu". Com conversa escolhida dá para ser exato, e aí ser exato é melhor.
   */
  const conversa = conversas.find((c) => c.atendimentoId === atendimentoId);
  const janelaDaConversa = conversa
    ? (canais.find((c) => c.canal === conversa.canal && c.modo === conversa.modo)?.janelaHoras ?? null)
    : janela;

  const foraDaJanela =
    tipo === "mensagem" &&
    janelaDaConversa !== null &&
    Number.isFinite(emMinutos) &&
    emMinutos > janelaDaConversa * 60;

  const criar = async () => {
    setCriando(true);
    setErro(null);
    const r = await criarAgendamento(cliente, {
      tipo,
      // `new Date(valor local).toISOString()` — o campo é local e o servidor guarda o instante. Mandar a
      // string do campo faria o servidor interpretá-la como UTC.
      quando: quando ? new Date(quando).toISOString() : null,
      texto,
      ...(atendimentoId ? { atendimentoId } : {}),
    });
    setCriando(false);
    if (r.ok) {
      setTexto("");
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível agendar.");
  };

  const pendentes = itens.filter((i) => i.estado === "pendente");
  const falhados = itens.filter((i) => i.estado === "falhou");

  const motivo =
    tipo === "mensagem" && atendimentoId === ""
      ? "Escolha a conversa por onde a mensagem sai."
      : passou
        ? "O horário já passou."
        : texto.trim() === ""
          ? "Escreva o texto."
          : null;
  const desabilitado = criando || motivo !== null;

  const formulario = (
    <div className="hugi-agenda-form">
      <span role="group" aria-label="Tipo de agendamento" className="hugi-agenda-tipo">
        <button type="button" aria-pressed={tipo === "lembrete"} onClick={() => setTipo("lembrete")}>
          Lembrete interno
        </button>
        <button type="button" aria-pressed={tipo === "mensagem"} onClick={() => setTipo("mensagem")}>
          Mensagem para o contato
        </button>
      </span>

      {tipo === "mensagem" && (
        <label className="hugi-agenda-campo">
          <span className="hugi-agenda-rotulo">
            Em qual conversa
            <Dica texto="A mensagem sai pela conversa: é ela que diz por qual canal e para qual número. Conversa encerrada é reaberta no disparo, como faria uma pessoa dando seguimento." />
          </span>
          <select className="hugi-agenda-controle" value={atendimentoId} onChange={(e) => setAtendimentoId(e.target.value)}>
            <option value="">Escolha a conversa</option>
            {conversas.map((c) => (
              <option key={c.atendimentoId} value={c.atendimentoId}>
                {c.contatoNome ?? "sem nome"} · {c.protocolo}
                {c.estado === "finalizado" ? " (encerrada — será reaberta)" : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="hugi-agenda-campo">
        <span className="hugi-agenda-rotulo">Quando</span>
        <input type="datetime-local" className="hugi-agenda-controle is-curto" value={quando} onChange={(e) => setQuando(e.target.value)} />
        {passou && (
          <span className="hugi-agenda-erro">Esse horário já passou. Para enviar agora, use o campo de mensagem da conversa.</span>
        )}
        {foraDaJanela && (
          <span className="hugi-agenda-aviso">
            Esse horário está a mais de {janelaDaConversa} horas daqui. Neste canal a conversa fecha{" "}
            {janelaDaConversa} horas depois da última mensagem do contato — se ele não escrever nesse
            meio-tempo, texto livre não sai, e só um template aprovado alcança.
          </span>
        )}
      </label>

      <label className="hugi-agenda-campo">
        <span className="hugi-agenda-rotulo">{tipo === "lembrete" ? "O que lembrar" : "O que enviar"}</span>
        <textarea
          className="hugi-agenda-controle hugi-agenda-texto"
          value={texto}
          maxLength={4000}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={
            tipo === "lembrete"
              ? "Ligar para a dona Maria sobre o resultado do exame"
              : "Olá! Passando para lembrar da sua consulta amanhã às 14h."
          }
        />
      </label>

      {erro && <p className="hugi-agenda-erro" role="alert">{erro}</p>}
    </div>
  );

  const rodape = (
    <div className="hugi-agenda-rodape">
      {motivo !== null && <Motivo id={idDoMotivo}>{motivo}</Motivo>}
      <button
        type="button"
        className="v2-botao is-primario"
        onClick={() => void criar()}
        disabled={desabilitado}
        {...(motivo !== null ? { "aria-describedby": idDoMotivo, title: motivo } : {})}
      >
        {tipo === "lembrete" ? "Criar lembrete" : "Agendar mensagem"}
      </button>
    </div>
  );

  const agendados = (
    <div className="hugi-agenda-agendados">
      {falhados.length > 0 && (
        <section aria-label="Não saiu">
          <h3>Não saiu</h3>
          {/*
            Os que falharam vêm ANTES dos pendentes, e ficam na tela.
            O motivo é o §7.3 da spec 05 na prática: uma saída automática barrada é a coisa que o cliente
            precisa ver, e a que todo produto do gênero esconde num log. `falhou` é terminal — repetir
            entregaria "lembrando da sua consulta de hoje" às 23h — então quem decide reagendar é gente.
          */}
          {falhados.map((i) => (
            <Item key={i.id} item={i} aoCancelar={null} />
          ))}
        </section>
      )}

      <section aria-label="Pendentes">
        <h3>Pendentes</h3>
        {pendentes.length === 0 ? (
          <Vazio>
            Nada agendado.
            <Dica texto="Crie um lembrete no formulário ao lado, ou escolha uma conversa e agende uma mensagem para o contato." />
          </Vazio>
        ) : (
          pendentes.map((i) => (
            <Item
              key={i.id}
              item={i}
              aoCancelar={() => void cancelarAgendamento(cliente, i.id)}
              aoConcluir={() => void concluirAgendamento(cliente, i.id)}
            />
          ))
        )}
      </section>
    </div>
  );

  return (
    <Pagina>
      <GradeDeCartoes
        rotulo="Agenda"
        // A LISTA PRIMEIRO E LARGA (spec 23, J5): o que se volta a ver é o que está agendado; o formulário é curto e
        // fica à direita, estreito. No celular, o primeiro cartão é a lista.
        colunas="minmax(0, 2fr) minmax(20rem, 1fr)"
        cartoes={[
          {
            id: "agendados",
            titulo: "Agendados",
            contagem: pendentes.length + falhados.length,
            conteudo: agendados,
          },
          { id: "novo", titulo: "Novo agendamento", rotuloCurto: "Novo", conteudo: formulario, rodape },
        ]}
      />
    </Pagina>
  );
}

function Item({ item, aoCancelar, aoConcluir }: { item: ItemDaAgenda; aoCancelar: (() => void) | null; aoConcluir?: (() => void) | null }) {
  return (
    <div className="hugi-agenda-item">
      <Selo tom={item.estado === "falhou" ? "atencao" : "neutro"}>{item.tipo === "lembrete" ? "Lembrete" : "Mensagem"}</Selo>
      <div className="hugi-agenda-item-texto">
        <p>{item.texto}</p>
        <p className="hugi-agenda-item-meta">
          {new Date(item.quando).toLocaleString("pt-BR")}
          {item.contatoNome ? ` · ${item.contatoNome}` : ""}
          {item.protocolo ? ` · ${item.protocolo}` : ""}
          {item.templateNome ? ` · template ${item.templateNome}` : ""}
        </p>
        {item.erro && (
          // O motivo fica ao LADO do item, e não num detalhe escondido: é a única coisa que responde
          // "por que meu cliente não recebeu".
          <p className="hugi-agenda-erro">{item.erro}</p>
        )}
      </div>
      {/* Lembrete vencido não some sozinho — o agendador não sabe se alguém ligou. Quem sabe marca. */}
      {aoConcluir && item.tipo === "lembrete" && (
        <button type="button" className="v2-botao is-primario" onClick={aoConcluir}>Feito</button>
      )}
      {aoCancelar && (
        <button type="button" className="v2-botao" onClick={aoCancelar}>
          Cancelar
        </button>
      )}
    </div>
  );
}
